'use client';

import { cn } from '@/shadcn/lib/utils';
import { CloseIcon } from '@/shared/components/icons';
import { Clock } from 'lucide-react';
import { Location } from '../types';
import { useRecentLocations, type RecentLocation } from '../utils/recentLocations';

interface Props {
    /** Aktualne vybrane lokality (formik.values.locations). */
    value: Location[];
    onChange: (next: Location[]) => void;
    /** Navrhy z vyhladavania (uz bez tych vybranych). */
    displayLocations: Location[];
    /** Uzivatel uz nieco pisal - rozlisuje "ziadne vysledky" od uvodneho stavu. */
    startedSearchLocations: boolean;
}

const ROW = cn(
    'w-full text-[13px] font-[500] py-[9px] px-[14px] cursor-pointer rounded-[12px]',
    'hover:bg-[#f6f5f0] transition duration-200 ease-[var(--ease-rx)]',
);

/**
 * Obsah pulldownu "Vyberte miesto" - vybrane lokality, navrhy z vyhladavania
 * a (kym sa nezacne pisat) posledne vyhladavane lokality z localStorage.
 *
 * Zdielane medzi inline filtrom a rozsirenym dialogom.
 */
export function LocationOptions(props: Props) {
    const { recent, remember, clear } = useRecentLocations();

    const selectedSlugs = new Set(props.value.map((l) => l.slug));
    /* Posledne hladane bez tych, co uz su vybrane. */
    const recentAvailable = recent.filter((r) => !selectedSlugs.has(r.slug));

    function select(loc: Location | RecentLocation) {
        props.onChange([...props.value, loc as Location]);
        remember(loc as RecentLocation);
    }

    return (
        <div className="w-full">
            {props.value.length > 0 && (
                <ul className="flex gap-[7px] flex-wrap mt-[12px]">
                    {props.value.map((loc, idx) => (
                        <li
                            key={idx}
                            className={cn(
                                'text-[11px] font-[600] rounded-[10px] py-[3px] px-[8px] bg-[#f3f3f3] w-fit',
                                'flex items-center gap-[5px]',
                            )}
                        >
                            {loc.name}
                            <CloseIcon
                                className="size-[7px] cursor-pointer"
                                onClick={() =>
                                    props.onChange(
                                        props.value.filter((i) => i.slug !== loc.slug),
                                    )
                                }
                            />
                        </li>
                    ))}
                </ul>
            )}

            <div className="w-full mt-[10px]">
                {props.displayLocations.length > 0 ? (
                    <ul className="w-full flex flex-col gap-[4px]">
                        {props.displayLocations.map((loc, idx) => (
                            <li key={idx} className={ROW} onClick={() => select(loc)}>
                                {loc.name}
                            </li>
                        ))}
                    </ul>
                ) : props.startedSearchLocations ? (
                    <div className="p-[10px] text-[14px] text-center">
                        <p>Neboli nájdené žiadne výsledky</p>
                    </div>
                ) : recentAvailable.length > 0 ? (
                    /* este sa nepisalo - ponukni posledne hladane */
                    <div className="w-full">
                        <div className="flex items-center justify-between px-[12px] pb-[4px]">
                            <p className="text-[11px] font-[700] uppercase tracking-[0.04em] text-black/40">
                                Posledné hľadané
                            </p>
                            <button
                                type="button"
                                className={cn(
                                    'text-[11px] font-[600] text-black/40 cursor-pointer',
                                    'hover:text-black transition duration-200 ease-[var(--ease-rx)]',
                                )}
                                onClick={clear}
                            >
                                Vymazať
                            </button>
                        </div>
                        <ul className="w-full flex flex-col gap-[4px]">
                            {recentAvailable.map((loc) => (
                                <li
                                    key={loc.slug}
                                    className={cn(ROW, 'flex items-center gap-[9px]')}
                                    onClick={() => select(loc)}
                                >
                                    <Clock className="size-[14px] shrink-0 text-black/35" />
                                    {loc.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <div className="p-[10px] text-[14px] text-center">
                        <p>Začnite písať mesto alebo obec…</p>
                    </div>
                )}
            </div>
        </div>
    );
}
